import { useMemo, useRef, useState } from 'react';
import {
  Inbox,
  Users,
  Plus,
  X,
  Search,
  UserPlus,
  FileUp,
  Download,
  Phone,
  Mail,
  CalendarClock,
  Zap,
  FileText,
  Euro,
  Clock,
} from 'lucide-react';
import clsx from 'clsx';
import {
  useAddNota,
  useClient,
  useClients,
  useConvertLead,
  useImportRows,
  useLeads,
  useLeadEstudio,
  useRegistrarComisionServicio,
  useSaveClient,
  useSetLeadEstado,
  type Client,
  type Lead,
} from '../api/clients';
import { useResolverVinculacion, useSolicitudesVinculacion } from '../api/portal';
import { leerArchivo, parseCsv, PLANTILLAS, rowToClient, rowToProperty } from '../lib/csv';
import { AyudaGuia } from '../components/Ayuda';

const PERFIL_STYLE: Record<string, string> = {
  propietario: 'bg-leaf/40 text-deep',
  inquilino: 'bg-sky-100 text-sky-800',
  comprador: 'bg-amber-100 text-amber-800',
  inversor: 'bg-violet-100 text-violet-800',
};

// Cómo se lee cada estado. «Descartada» y no «cerrado»: «Cerrar» es lo que se
// hace con una operación ganada, y la misma palabra no puede decir lo contrario.
const LEAD_LABEL: Record<Lead['estado'], string> = {
  nuevo: 'Nueva',
  contactado: 'Contactada',
  cerrado: 'Descartada',
  convertido: 'Pasada a cliente',
};

const LEAD_STYLE: Record<Lead['estado'], string> = {
  nuevo: 'bg-leaf/40 text-deep',
  contactado: 'bg-amber-100 text-amber-800',
  cerrado: 'bg-neutral-200 text-neutral-600',
  convertido: 'bg-sky-100 text-sky-800',
};

/** Inquilinos que se registraron con un email que no cuadra con ningún contrato
 *  y piden que les vinculen su vivienda a mano.
 *
 *  Aceptar aquí solo cierra la solicitud: lo que de verdad le da acceso es dar
 *  de alta el contrato en la ficha de la vivienda. Por eso el texto del botón
 *  dice a dónde hay que ir después. */
function VinculacionesPendientes() {
  const { data: solicitudes } = useSolicitudesVinculacion();
  const resolver = useResolverVinculacion();

  if (!solicitudes?.length) return null;

  return (
    <section className="mt-5 rounded-2xl border-2 border-sky-300 bg-sky-50/60 p-4">
      <h2 className="font-extrabold text-sky-900">
        Inquilinos esperando que les vinculemos su vivienda ({solicitudes.length})
      </h2>
      <div className="mt-3 space-y-2">
        {solicitudes.map((c) => (
          <article
            key={c.client_id}
            className="rounded-xl border border-sky-200 bg-white p-3 text-sm"
          >
            <p className="font-bold text-deep">
              {c.nombre} <span className="font-normal text-ink/50">· {c.email}</span>
            </p>
            <p className="mt-1 text-ink/70">
              Dice vivir en <strong>{c.vinculacion_solicitada?.direccion}</strong>
            </p>
            {c.vinculacion_solicitada?.mensaje && (
              <p className="mt-1 text-ink/60">«{c.vinculacion_solicitada.mensaje}»</p>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                disabled={resolver.isPending}
                onClick={() => resolver.mutate({ clientId: c.client_id, aceptar: true })}
                className="rounded-full bg-brand px-4 py-1.5 text-xs font-bold text-white transition hover:bg-deep disabled:opacity-50"
              >
                Comprobado — ahora doy de alta el contrato
              </button>
              <button
                disabled={resolver.isPending}
                onClick={() => resolver.mutate({ clientId: c.client_id, aceptar: false })}
                className="rounded-full border border-deep/15 px-4 py-1.5 text-xs font-bold text-deep transition hover:border-red-300 hover:text-red-700 disabled:opacity-50"
              >
                No es cliente
              </button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

export default function ClientesView() {
  // `?ficha=<client_id>` (lo usa «Ver ficha» en Cuentas) abre la Cartera con esa
  // ficha abierta.
  const fichaInicial = new URLSearchParams(window.location.search).get('ficha');
  const [tab, setTab] = useState<'solicitudes' | 'clientes'>(fichaInicial ? 'clientes' : 'solicitudes');
  const { data: leads } = useLeads();
  const nuevas = leads?.filter((l) => l.estado === 'nuevo').length ?? 0;

  return (
    <div className="pb-16 md:pb-0">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-extrabold tracking-tight text-deep">Clientes</h1>
        <AyudaGuia seccion="clientes" />
      </div>

      <div className="mt-5 flex gap-2">
        <TabBtn active={tab === 'solicitudes'} onClick={() => setTab('solicitudes')}>
          <Inbox size={15} /> Solicitudes
          {nuevas > 0 && (
            <span className="ml-1 rounded-full bg-brand px-2 py-0.5 text-[0.65rem] font-bold text-white">
              {nuevas}
            </span>
          )}
        </TabBtn>
        <TabBtn active={tab === 'clientes'} onClick={() => setTab('clientes')}>
          <Users size={15} /> Cartera
        </TabBtn>
      </div>

      {/* Va fuera de las pestañas: son pocas, urgen y si se esconden detrás de
          una pestaña se quedan sin atender. */}
      <VinculacionesPendientes />

      {tab === 'solicitudes' ? <LeadsPanel /> : <ClientesPanel fichaInicial={fichaInicial} />}
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition',
        active ? 'bg-deep text-white' : 'bg-surface text-ink/60 border border-deep/15 hover:text-deep',
      )}
    >
      {children}
    </button>
  );
}

// ── Solicitudes ───────────────────────────────────────────────────

const eur = (n?: number) => (n || n === 0 ? `${n.toLocaleString('es-ES')} €` : '—');

// La categoría se DERIVA del lead, no es un campo nuevo: los `servicio` con
// estudio de factura son suministros, los que traen una simulación son hipoteca,
// y el resto asesoría. Solo se filtran los tipos que la web genera de verdad —
// no hay "creación de cuenta" (autoregistro) ni "visita" (nace en Operaciones).
type Categoria = 'suministros' | 'hipoteca' | 'asesoria' | 'te-llamamos' | 'inversor' | 'otros';

function categoriaLead(l: Lead): Categoria {
  if (l.tipo === 'servicio') {
    if (l.bill_id || l.ahorro_anual != null) return 'suministros';
    // Se mira `precio_vivienda` y no `cuota_estimada` a propósito: cuando el
    // ahorro no daba para la operación no hay cuota, y ese lead —alguien a
    // quien le faltan 14.000 €— es justamente de los que hay que atender.
    if (l.precio_vivienda != null) return 'hipoteca';
    return 'asesoria';
  }
  if (l.tipo === 'te-llamamos') return 'te-llamamos';
  if (l.tipo === 'inversor') return 'inversor';
  return 'otros';
}

const CATEGORIA_LABEL: Record<Categoria, string> = {
  suministros: 'Suministros',
  hipoteca: 'Hipoteca',
  asesoria: 'Asesoría',
  'te-llamamos': 'Te llamamos',
  inversor: 'Inversor',
  otros: 'Otros',
};
// El orden en que se ofrecen los chips (los que no tengan ningún lead no se pintan).
const CATEGORIA_ORDEN: Categoria[] = [
  'suministros',
  'hipoteca',
  'asesoria',
  'te-llamamos',
  'inversor',
  'otros',
];

// «Días sin acción» solo cuenta mientras el lead está `nuevo`: en cuanto se marca
// contactado / convertido / cerrado ya se actuó, y deja de ser backlog.
function diasSinAccion(l: Lead): number | null {
  if (l.estado !== 'nuevo') return null;
  const ts = Number(l.created_at);
  if (!ts) return null;
  return Math.floor((Date.now() / 1000 - ts) / 86400);
}

function LeadsPanel() {
  const { data: leads, isLoading } = useLeads();
  const setEstado = useSetLeadEstado();
  const convert = useConvertLead();
  const [estudioLead, setEstudioLead] = useState<Lead | null>(null);
  const [comisionLead, setComisionLead] = useState<Lead | null>(null);
  const [filtro, setFiltro] = useState<Categoria | 'todos'>('todos');

  if (isLoading) return <p className="mt-8 text-sm text-ink/50">Cargando…</p>;
  if (!leads?.length)
    return (
      <div className="mt-6 rounded-2xl border border-dashed border-deep/20 bg-surface px-6 py-12 text-center text-sm text-ink/55">
        Sin solicitudes todavía. Cada formulario enviado desde la web aparecerá aquí.
      </div>
    );

  // Contador por categoría (sobre TODAS las solicitudes) para las chapas de filtro.
  const counts = leads.reduce<Record<string, number>>((acc, l) => {
    const c = categoriaLead(l);
    acc[c] = (acc[c] ?? 0) + 1;
    return acc;
  }, {});

  const visibles = filtro === 'todos' ? leads : leads.filter((l) => categoriaLead(l) === filtro);

  // Orden = backlog de prioridad: primero lo que espera acción (nuevo, luego
  // contactado, luego lo cerrado); dentro de `nuevo`, lo más antiguo arriba (más
  // días sin responder); el resto, lo más reciente arriba.
  const rank = (l: Lead) => (l.estado === 'nuevo' ? 0 : l.estado === 'contactado' ? 1 : 2);
  const ordered = [...visibles].sort((a, b) => {
    const r = rank(a) - rank(b);
    if (r) return r;
    const da = diasSinAccion(a);
    const db = diasSinAccion(b);
    if (da != null && db != null) return db - da;
    return Number(b.created_at) - Number(a.created_at);
  });

  return (
    <>
      {/* Filtro por tipo: una sola lista, no secciones. Solo se ofrecen los tipos
          que tienen alguna solicitud, para no dar un filtro que no lleva a nada. */}
      <div className="mt-5 flex flex-wrap gap-2">
        <FiltroChip label="Todos" count={leads.length} active={filtro === 'todos'} onClick={() => setFiltro('todos')} />
        {CATEGORIA_ORDEN.filter((c) => counts[c]).map((c) => (
          <FiltroChip
            key={c}
            label={CATEGORIA_LABEL[c]}
            count={counts[c]}
            active={filtro === c}
            onClick={() => setFiltro(c)}
          />
        ))}
      </div>

      <div className="mt-4 space-y-3">
      {ordered.map((l) => {
        const esSuministro = l.tipo === 'servicio';
        const esHipoteca = categoriaLead(l) === 'hipoteca';
        const dias = diasSinAccion(l);
        return (
          <article key={l.lead_id} className="rounded-2xl border border-deep/10 bg-surface p-4 sm:p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={clsx('rounded-full px-2.5 py-0.5 text-[0.65rem] font-bold uppercase tracking-wider', LEAD_STYLE[l.estado])}>
                    {LEAD_LABEL[l.estado] ?? l.estado}
                  </span>
                  {/* Antigüedad: solo mientras está sin tocar. Rojo a partir de 2
                      días — es el backlog que hay que atender antes. */}
                  {dias != null && (
                    <span
                      className={clsx(
                        'flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[0.65rem] font-bold uppercase tracking-wider',
                        dias > 2 ? 'bg-red-100 text-red-700' : 'bg-soft text-ink/50',
                      )}
                    >
                      <Clock size={11} /> {dias === 0 ? 'hoy' : `${dias} d sin responder`}
                    </span>
                  )}
                  <span className="rounded-full border border-deep/15 px-2 py-0.5 text-[0.68rem] font-bold uppercase tracking-wider text-ink/50">
                    {CATEGORIA_LABEL[categoriaLead(l)]}
                  </span>
                  {(l.servicios_nombres?.length
                    ? l.servicios_nombres
                    : [l.servicio_nombre ?? (l.tipo === 'te-llamamos' ? '¿Te llamamos?' : l.tipo)]
                  ).map((s) => (
                    <span
                      key={s}
                      className="rounded-full bg-brand/10 px-2 py-0.5 text-[0.68rem] font-bold uppercase tracking-wider text-brand"
                    >
                      {s}
                    </span>
                  ))}
                  {l.origen && <span className="text-[0.65rem] text-ink/35">· {l.origen}</span>}
                </div>
                <p className="mt-1.5 font-bold text-deep">{l.nombre}</p>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-ink/60">
                  {l.telefono && <span className="flex items-center gap-1"><Phone size={11} /> {l.telefono}</span>}
                  {l.email && <span className="flex items-center gap-1"><Mail size={11} /> {l.email}</span>}
                  {(l.fecha || l.hora) && (
                    <span className="flex items-center gap-1"><CalendarClock size={11} /> {l.fecha} {l.hora}</span>
                  )}
                </div>
                {/* La chapa de suministros: ahorro y comercializadora actual, para
                    priorizar sin abrir nada. */}
                {esSuministro && (l.ahorro_anual != null || l.comercializadora) && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {l.ahorro_anual != null && (
                      <span className="flex items-center gap-1 rounded-full bg-leaf/40 px-2.5 py-0.5 text-xs font-bold text-deep">
                        <Zap size={12} /> ahorro {eur(l.ahorro_anual)}/año
                      </span>
                    )}
                    {l.comercializadora && (
                      <span className="text-xs text-ink/55">actual: {l.comercializadora}</span>
                    )}
                    {l.commission_id && (
                      <span className="flex items-center gap-1 rounded-full bg-brand/15 px-2.5 py-0.5 text-xs font-bold text-brand">
                        <Euro size={12} /> comisión registrada
                      </span>
                    )}
                  </div>
                )}
                {/* La chapa de hipoteca: lo que simuló, para llamarle sabiendo
                    de qué se habla. Sin cuota significa que el ahorro no le
                    llegaba — y eso se dice con la cifra que le falta, porque es
                    la conversación entera. */}
                {esHipoteca && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {l.cuota_estimada ? (
                      <span className="flex items-center gap-1 rounded-full bg-leaf/40 px-2.5 py-0.5 text-xs font-bold text-deep">
                        <Euro size={12} /> cuota {eur(l.cuota_estimada)}/mes
                      </span>
                    ) : (
                      <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-bold text-amber-800">
                        sin ahorro suficiente
                      </span>
                    )}
                    <span className="text-xs text-ink/55">
                      {eur(l.precio_vivienda)}
                      {l.plazo_anios ? ` · ${l.plazo_anios} años` : ''}
                      {l.entrada != null ? ` · ahorrado ${eur(l.entrada)}` : ''}
                    </span>
                    {l.commission_id && (
                      <span className="flex items-center gap-1 rounded-full bg-brand/15 px-2.5 py-0.5 text-xs font-bold text-brand">
                        <Euro size={12} /> comisión registrada
                      </span>
                    )}
                  </div>
                )}
                {l.mensaje && <p className="mt-2 text-sm text-ink/70">{l.mensaje}</p>}
              </div>
              <div className="flex shrink-0 flex-wrap gap-2">
                {esSuministro && l.bill_id && (
                  <ActionBtn onClick={() => setEstudioLead(l)}>
                    <FileText size={13} /> Ver estudio
                  </ActionBtn>
                )}
                {esSuministro && !l.commission_id && (
                  <ActionBtn primary onClick={() => setComisionLead(l)}>
                    <Euro size={13} /> Registrar comisión
                  </ActionBtn>
                )}
                {l.estado === 'nuevo' && (
                  <ActionBtn onClick={() => setEstado.mutate({ leadId: l.lead_id, estado: 'contactado' })}>
                    Marcar contactado
                  </ActionBtn>
                )}
                {l.estado !== 'convertido' && (
                  <ActionBtn primary={!esSuministro} onClick={() => convert.mutate(l.lead_id)}>
                    <UserPlus size={13} /> A cliente
                  </ActionBtn>
                )}
                {l.estado !== 'cerrado' && l.estado !== 'convertido' && (
                  <ActionBtn onClick={() => setEstado.mutate({ leadId: l.lead_id, estado: 'cerrado' })}>
                    Descartar
                  </ActionBtn>
                )}
              </div>
            </div>
          </article>
        );
      })}

      {estudioLead && <EstudioModal lead={estudioLead} onClose={() => setEstudioLead(null)} />}
      {comisionLead && <ComisionModal lead={comisionLead} onClose={() => setComisionLead(null)} />}
      </div>
    </>
  );
}

function FiltroChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold transition',
        active ? 'bg-brand text-white' : 'bg-soft text-ink/55 hover:text-deep',
      )}
    >
      {label}
      <span className={clsx('rounded-full px-1.5 text-[0.65rem]', active ? 'bg-white/25' : 'bg-deep/10')}>
        {count}
      </span>
    </button>
  );
}

// El estudio que originó un lead de suministros. Sin CUPS ni dirección: el
// backend ya los filtra, aquí solo se pintan las cifras que se devuelven.
function EstudioModal({ lead, onClose }: { lead: Lead; onClose: () => void }) {
  const { data: e, isLoading, isError } = useLeadEstudio(lead.lead_id);
  const est = (e?.estudio ?? {}) as Record<string, unknown>;
  const num = (v: unknown) => (typeof v === 'number' ? v : undefined);

  return (
    <Modal title={`Estudio · ${lead.nombre}`} onClose={onClose}>
      {isLoading ? (
        <p className="text-sm text-ink/50">Cargando estudio…</p>
      ) : isError || !e ? (
        <p className="text-sm text-red-700">No se pudo cargar el estudio.</p>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Cifra k="Ahorro anual" v={`${eur(num(est.ahorro_anual))}/año`} destacada />
            <Cifra k="Ahorro" v={est.ahorro_pct != null ? `${est.ahorro_pct}%` : '—'} />
            <Cifra k="Paga ahora" v={`${eur(num(est.pagado_anual))}/año`} />
            <Cifra k="Consumo" v={est.consumo_total_kwh != null ? `${est.consumo_total_kwh} kWh` : '—'} />
          </div>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 border-t border-deep/10 pt-4 text-sm">
            <Dato k="Comercializadora" v={e.datos.comercializadora as string} />
            <Dato k="Tarifa" v={e.datos.nombre_tarifa as string} />
            <Dato k="Importe factura" v={e.datos.importe_total != null ? eur(Number(e.datos.importe_total)) : undefined} />
            <Dato k="Suministro" v={e.suministro} />
          </dl>
          <p className="text-[0.7rem] text-ink/40">
            El estudio no incluye datos personales (CUPS ni dirección). Bill {e.bill_id}.
          </p>
        </div>
      )}
    </Modal>
  );
}

function Cifra({ k, v, destacada }: { k: string; v: string; destacada?: boolean }) {
  return (
    <div className={clsx('rounded-2xl border px-4 py-3', destacada ? 'border-leaf bg-leaf/15' : 'border-deep/10 bg-surface')}>
      <p className={clsx('text-xl font-extrabold', destacada ? 'text-deep' : 'text-ink/80')}>{v}</p>
      <p className="text-[0.65rem] font-bold uppercase tracking-wider text-ink/45">{k}</p>
    </div>
  );
}

function Dato({ k, v }: { k: string; v?: string }) {
  return (
    <div>
      <dt className="text-[0.65rem] font-bold uppercase tracking-wider text-ink/40">{k}</dt>
      <dd className="font-semibold text-deep">{v || '—'}</dd>
    </div>
  );
}

// Registrar la comisión del socio con quien se tramita el servicio. El importe
// se pone a mano: el % del socio vive como texto libre en el catálogo.
function ComisionModal({ lead, onClose }: { lead: Lead; onClose: () => void }) {
  const registrar = useRegistrarComisionServicio();
  const [importe, setImporte] = useState<number>(0);

  const submit = async () => {
    await registrar.mutateAsync({ leadId: lead.lead_id, importe });
    onClose();
  };

  return (
    <Modal title={`Comisión de servicio · ${lead.nombre}`} onClose={onClose}>
      <div className="space-y-5">
        <p className="text-sm text-ink/60">
          {lead.servicio_nombre || lead.servicio || 'Servicio'}
          {lead.comercializadora ? ` · ${lead.comercializadora}` : ''}
        </p>
        <label className="block">
          <span className="block text-xs font-bold uppercase tracking-wider text-ink/50">Importe de la comisión €</span>
          <input
            type="number"
            value={importe}
            onChange={(ev) => setImporte(Number(ev.target.value))}
            className="mt-1 w-full rounded-xl border border-deep/15 bg-soft px-3.5 py-2.5 text-base outline-none focus:border-brand transition"
          />
          <span className="mt-1 block text-[0.7rem] text-ink/40">
            Lo que paga el socio por esta alta. Queda contabilizado en Comisiones.
          </span>
        </label>
        <div className="flex items-center gap-3">
          <button
            onClick={submit}
            disabled={registrar.isPending || importe <= 0}
            className="rounded-full bg-deep px-7 py-2.5 text-sm font-bold text-white transition hover:bg-brand disabled:opacity-50"
          >
            {registrar.isPending ? 'Registrando…' : 'Registrar comisión'}
          </button>
          {registrar.isError && (
            <span className="text-sm text-red-700">
              {(registrar.error as Error)?.message ?? 'No se pudo registrar. Reintenta.'}
            </span>
          )}
        </div>
      </div>
    </Modal>
  );
}

function ActionBtn({
  primary,
  onClick,
  children,
}: {
  primary?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold transition',
        primary
          ? 'bg-brand text-white hover:bg-deep'
          : 'border border-deep/20 text-deep hover:bg-soft',
      )}
    >
      {children}
    </button>
  );
}

// ── Cartera de clientes ───────────────────────────────────────────

function ClientesPanel({ fichaInicial }: { fichaInicial?: string | null }) {
  const { data: clients, isLoading } = useClients();
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<string | null>(fichaInicial ?? null);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return clients;
    return clients?.filter((c) =>
      [c.nombre, c.email, c.telefono].some((v) => v?.toLowerCase().includes(needle)),
    );
  }, [clients, q]);

  return (
    <div className="mt-6">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex min-w-52 flex-1 items-center gap-2 rounded-full border border-deep/15 bg-surface px-4 py-2">
          <Search size={15} className="text-ink/40" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre, email o teléfono"
            className="w-full bg-transparent text-sm outline-none placeholder:text-ink/35"
          />
        </div>
        <button
          onClick={() => setImporting(true)}
          className="flex items-center gap-2 rounded-full border-2 border-deep px-4 py-2 text-xs font-bold text-deep transition hover:bg-deep hover:text-white"
        >
          <FileUp size={14} /> Importar cartera
        </button>
        <button
          onClick={() => setCreating(true)}
          className="flex items-center gap-2 rounded-full bg-brand px-4 py-2 text-xs font-bold text-white transition hover:bg-deep"
        >
          <Plus size={14} /> Nuevo cliente
        </button>
      </div>

      {isLoading ? (
        <p className="mt-8 text-sm text-ink/50">Cargando…</p>
      ) : !filtered?.length ? (
        <div className="mt-6 rounded-2xl border border-dashed border-deep/20 bg-surface px-6 py-12 text-center text-sm text-ink/55">
          {q ? 'Sin resultados para esa búsqueda.' : 'Aún no hay clientes. Crea uno o importa tu cartera.'}
        </div>
      ) : (
        <div className="mt-5 overflow-hidden rounded-2xl border border-deep/10 bg-surface">
          {filtered.map((c, i) => (
            <button
              key={c.client_id}
              onClick={() => setOpenId(c.client_id)}
              className={clsx(
                'flex w-full flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-left transition hover:bg-soft sm:px-5',
                i > 0 && 'border-t border-deep/10',
              )}
            >
              <span className="min-w-40 font-bold text-deep">{c.nombre}</span>
              <span className="text-xs text-ink/55">{c.email || '—'}</span>
              <span className="text-xs text-ink/55">{c.telefono || ''}</span>
              <span className="ml-auto flex gap-1.5">
                {c.profile_types?.map((p) => (
                  <span key={p} className={clsx('rounded-full px-2 py-0.5 text-[0.62rem] font-bold uppercase tracking-wider', PERFIL_STYLE[p] ?? 'bg-soft text-ink/60')}>
                    {p}
                  </span>
                ))}
              </span>
            </button>
          ))}
        </div>
      )}

      {creating && (
        <Modal title="Nuevo cliente" onClose={() => setCreating(false)}>
          <ClientForm onDone={() => setCreating(false)} />
        </Modal>
      )}
      {openId && <ClientDetail id={openId} onClose={() => setOpenId(null)} />}
      {importing && <ImportModal onClose={() => setImporting(false)} />}
    </div>
  );
}

function ClientForm({ initial, onDone }: { initial?: Client; onDone: () => void }) {
  const save = useSaveClient();
  const [form, setForm] = useState({
    nombre: initial?.nombre ?? '',
    email: initial?.email ?? '',
    telefono: initial?.telefono ?? '',
    notas: initial?.notas ?? '',
    profile_types: initial?.profile_types ?? [],
  });

  const toggle = (p: string) =>
    setForm((f) => ({
      ...f,
      profile_types: f.profile_types.includes(p)
        ? f.profile_types.filter((x) => x !== p)
        : [...f.profile_types, p],
    }));

  const field =
    'mt-1 w-full rounded-xl border border-deep/15 bg-soft px-3.5 py-2.5 text-base outline-none focus:border-brand transition';

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        await save.mutateAsync({ ...form, client_id: initial?.client_id });
        onDone();
      }}
      className="grid gap-4"
    >
      <label className="block">
        <span className="text-xs font-bold uppercase tracking-wider text-ink/50">Nombre *</span>
        <input required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} className={field} />
      </label>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-wider text-ink/50">Email</span>
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} />
        </label>
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-wider text-ink/50">Teléfono</span>
          <input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} className={field} />
        </label>
      </div>
      <div>
        <span className="text-xs font-bold uppercase tracking-wider text-ink/50">Perfiles</span>
        <div className="mt-2 flex flex-wrap gap-2">
          {['propietario', 'inquilino', 'comprador', 'inversor'].map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => toggle(p)}
              className={clsx(
                'rounded-full px-3.5 py-1.5 text-xs font-bold transition',
                form.profile_types.includes(p)
                  ? 'bg-deep text-white'
                  : 'border border-deep/20 text-ink/60 hover:text-deep',
              )}
            >
              {p}
            </button>
          ))}
        </div>
      </div>
      <label className="block">
        <span className="text-xs font-bold uppercase tracking-wider text-ink/50">Notas</span>
        <textarea rows={3} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} className={`${field} resize-none`} />
      </label>
      <button
        type="submit"
        disabled={save.isPending}
        className="rounded-full bg-brand px-7 py-2.5 text-sm font-bold text-white transition hover:bg-deep disabled:opacity-50"
      >
        {initial ? 'Guardar cambios' : 'Crear cliente'}
      </button>
    </form>
  );
}

function ClientDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const { data: c } = useClient(id);
  const addNota = useAddNota(id);
  const [editing, setEditing] = useState(false);
  const [nota, setNota] = useState('');

  if (!c) return null;

  return (
    <Modal title={c.nombre} onClose={onClose}>
      {editing ? (
        <ClientForm initial={c} onDone={() => setEditing(false)} />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            {c.profile_types?.map((p) => (
              <span key={p} className={clsx('rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider', PERFIL_STYLE[p] ?? 'bg-soft')}>
                {p}
              </span>
            ))}
            <button
              onClick={() => setEditing(true)}
              className="ml-auto rounded-full border-2 border-deep px-4 py-1.5 text-xs font-bold text-deep transition hover:bg-deep hover:text-white"
            >
              Editar
            </button>
          </div>
          <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-[0.65rem] font-bold uppercase tracking-wider text-ink/40">Email</dt>
              <dd className="font-semibold text-deep">{c.email || '—'}</dd>
            </div>
            <div>
              <dt className="text-[0.65rem] font-bold uppercase tracking-wider text-ink/40">Teléfono</dt>
              <dd className="font-semibold text-deep">{c.telefono || '—'}</dd>
            </div>
          </dl>
          {c.notas && <p className="mt-3 rounded-xl bg-soft p-3 text-sm text-ink/70">{c.notas}</p>}

          <p className="mt-6 text-xs font-bold uppercase tracking-widest text-brand">Historial</p>
          <div className="mt-2 max-h-56 space-y-2 overflow-y-auto">
            {[...(c.historial ?? [])].reverse().map((h, i) => (
              <div key={i} className="rounded-xl border border-deep/10 bg-surface px-3 py-2 text-sm">
                <span className="text-[0.65rem] text-ink/40">
                  {new Date(h.ts * 1000).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })}
                  {h.tipo !== 'nota' && ` · ${h.tipo}`}
                </span>
                <p className="text-ink/80">{h.texto}</p>
              </div>
            ))}
            {!c.historial?.length && <p className="text-sm text-ink/40">Sin actividad todavía.</p>}
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (nota.trim()) {
                addNota.mutate(nota);
                setNota('');
              }
            }}
            className="mt-3 flex gap-2"
          >
            <input
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              placeholder="Añadir nota (llamada, visita, acuerdo…)"
              className="w-full rounded-full border border-deep/15 bg-soft px-4 py-2 text-sm outline-none focus:border-brand"
            />
            <button className="shrink-0 rounded-full bg-deep px-4 py-2 text-xs font-bold text-white hover:bg-brand transition">
              Añadir
            </button>
          </form>
        </>
      )}
    </Modal>
  );
}

// ── Importador ────────────────────────────────────────────────────

function ImportModal({ onClose }: { onClose: () => void }) {
  const importRows = useImportRows();
  const fileRef = useRef<HTMLInputElement>(null);
  const [tipo, setTipo] = useState<'clientes' | 'viviendas'>('clientes');
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [result, setResult] = useState<{ creados: number; errores: { fila: number; error: string }[] } | null>(null);

  const plantilla = PLANTILLAS[tipo];

  const downloadTemplate = () => {
    const blob = new Blob([`${plantilla.cabecera}\n${plantilla.ejemplo}\n`], {
      type: 'text/csv;charset=utf-8',
    });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `plantilla-${tipo}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <Modal title="Importar cartera" onClose={onClose}>
      <div className="flex gap-2">
        {(['clientes', 'viviendas'] as const).map((t) => (
          <button
            key={t}
            onClick={() => {
              setTipo(t);
              setRows([]);
              setResult(null);
            }}
            className={clsx(
              'rounded-full px-4 py-1.5 text-xs font-bold transition',
              tipo === t ? 'bg-deep text-white' : 'border border-deep/20 text-ink/60',
            )}
          >
            {t === 'clientes' ? 'Clientes' : 'Viviendas'}
          </button>
        ))}
      </div>

      <p className="mt-4 text-sm text-ink/60">
        Descarga la plantilla, rellénala en Excel, Numbers o Google Sheets, guárdala como CSV y
        súbela aquí.
      </p>
      <p className="mt-1 text-xs text-ink/45">{plantilla.ayuda}</p>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          onClick={downloadTemplate}
          className="flex items-center gap-2 rounded-full border-2 border-deep px-4 py-2 text-xs font-bold text-deep transition hover:bg-deep hover:text-white"
        >
          <Download size={14} /> Plantilla {tipo}.csv
        </button>
        <button
          onClick={() => fileRef.current?.click()}
          className="flex items-center gap-2 rounded-full bg-brand px-4 py-2 text-xs font-bold text-white transition hover:bg-deep"
        >
          <FileUp size={14} /> Elegir archivo CSV
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          hidden
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            setRows(parseCsv(await leerArchivo(file)));
            setResult(null);
            e.target.value = '';
          }}
        />
      </div>

      {rows.length > 0 && !result && (
        <div className="mt-4">
          <p className="text-sm font-semibold text-deep">
            {rows.length} filas leídas — primera: “{Object.values(rows[0]).filter(Boolean).slice(0, 3).join(' · ')}”
          </p>
          <button
            onClick={async () => {
              const mapped = rows.map((r) => (tipo === 'clientes' ? rowToClient(r) : rowToProperty(r)));
              setResult(await importRows.mutateAsync({ tipo, rows: mapped }));
            }}
            disabled={importRows.isPending}
            className="mt-3 rounded-full bg-deep px-6 py-2.5 text-sm font-bold text-white transition hover:bg-brand disabled:opacity-50"
          >
            {importRows.isPending ? 'Importando…' : `Importar ${rows.length} ${tipo}`}
          </button>
        </div>
      )}

      {result && (
        <div className="mt-4 rounded-xl bg-soft p-4 text-sm">
          <p className="font-bold text-deep">✓ {result.creados} {tipo} importados</p>
          {result.errores.length > 0 && (
            <ul className="mt-2 space-y-1 text-xs text-red-700">
              {result.errores.map((er) => (
                // El servidor cuenta desde la primera fila de datos; en la hoja, esa
                // es la 2 (la 1 son los títulos). Se enseña el número de la hoja.
                <li key={er.fila}>Fila {er.fila + 1} de tu hoja: {er.error}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Modal>
  );
}

// ── Modal compartido ──────────────────────────────────────────────

function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-deep/40 p-0 sm:items-center sm:p-6" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="max-h-[92vh] w-full overflow-y-auto rounded-t-3xl bg-white p-6 sm:max-w-xl sm:rounded-3xl sm:p-7"
      >
        <div className="mb-5 flex items-center justify-between gap-4">
          <h2 className="truncate text-lg font-extrabold text-deep">{title}</h2>
          <button onClick={onClose} aria-label="Cerrar" className="rounded-full p-1.5 text-ink/50 hover:bg-soft">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
